import { useEffect, useRef, useState } from 'preact/hooks';
import { Sheet } from '../../../components/ui/Sheet.js';
import { Spinner } from '../../../components/ui/Spinner.js';
import { ApiError, api } from '../../../services/api.js';
import type { IdentitySide } from './groups.js';

interface DocumentPreviewSheetProps {
  side: IdentitySide;
  title: string;
  onClose: () => void;
}

/**
 * 059 R3: shows a submitted document inside the app.
 *
 * The admin panel opens documents with `window.open`, which is unreliable once this app is
 * installed to a home screen — there is no tab to open into. The document is streamed with
 * the session's credential, held as an object URL for exactly as long as it is on screen,
 * and released both when dismissed and when the screen goes away. Nothing about it is ever
 * written to browser storage or to the service-worker cache.
 */
export function DocumentPreviewSheet({ side, title, onClose }: DocumentPreviewSheetProps) {
  const [doc, setDoc] = useState<{ url: string; type: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const held = useRef<string | null>(null);

  /** Revoke exactly once, whoever gets there first — dismissal or unmount. Held in a ref
   *  rather than closed over, so both callers act on the one URL currently outstanding. */
  function release() {
    if (held.current) {
      URL.revokeObjectURL(held.current);
      held.current = null;
    }
  }

  useEffect(() => {
    let released = false;

    api
      .getBlobUrl(`/users/me/identity/${side}/file`)
      .then((loaded) => {
        if (released) {
          URL.revokeObjectURL(loaded.url);
          return;
        }
        held.current = loaded.url;
        setDoc(loaded);
      })
      .catch((err: unknown) => {
        if (released) return;
        // A stale screen can race a replacement, so an empty side is a statement of fact,
        // not a failure to report.
        if (err instanceof ApiError && err.status === 404) {
          setFailure('Nothing submitted for this side yet.');
          return;
        }
        setFailure(err instanceof Error ? err.message : 'Could not load document');
      });

    return () => {
      released = true;
      if (held.current) {
        URL.revokeObjectURL(held.current);
        held.current = null;
      }
    };
  }, [side]);

  return (
    <Sheet
      open
      title={title}
      onDismiss={() => {
        release();
        onClose();
      }}
    >
      {failure ? (
        <p className="py-6 text-center text-sm text-gray-400">{failure}</p>
      ) : !doc ? (
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      ) : doc.type === 'application/pdf' ? (
        <a
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          className="py-6 text-center text-sm font-medium text-primary"
        >
          Open document
        </a>
      ) : (
        <img src={doc.url} alt={title} className="w-full rounded-lg object-contain" />
      )}
    </Sheet>
  );
}
